import { LoaderCircle, X } from 'lucide-react'
import { useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import { normalizarDecimal } from '../../utils/numeros'

function generarCodigoInterno() {
  const sufijoAleatorio = Math.random().toString(36).slice(2, 7)
  return `ENV-${Date.now()}-${sufijoAleatorio}`
}

function CajaRejillaModal({ tipoEnvase, onClose, onGuardado }) {
  const { empresaActual } = useEmpresa()
  const esEdicion = Boolean(tipoEnvase)

  const [nombre, setNombre] = useState(tipoEnvase?.nombre || '')
  const [taraKg, setTaraKg] = useState(
    tipoEnvase?.tara_kg != null ? String(tipoEnvase.tara_kg) : ''
  )
  const [activo, setActivo] = useState(tipoEnvase?.activo ?? true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarTipoEnvase = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const nombreLimpio = nombre.trim()
    const taraNumero = normalizarDecimal(taraKg)

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre de la caja/rejilla.')
      return
    }

    if (taraKg.trim() === '' || !Number.isFinite(taraNumero)) {
      setError('Debes ingresar la tara en kilos.')
      return
    }

    if (taraNumero < 0) {
      setError('La tara no puede ser negativa.')
      return
    }

    try {
      setGuardando(true)

      let tipoEnvaseGuardado = null

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('tipos_envase')
          .update({
            nombre: nombreLimpio,
            tara_kg: taraNumero,
            activo,
          })
          .eq('id', tipoEnvase.id)
          .eq('empresa_id', empresaActual.id)
          .select()
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        tipoEnvaseGuardado = data
      } else {
        const { data, error: errorInsertar } = await supabase
          .from('tipos_envase')
          .insert({
            empresa_id: empresaActual.id,
            codigo: generarCodigoInterno(),
            nombre: nombreLimpio,
            tara_kg: taraNumero,
            activo: true,
          })
          .select()
          .single()

        if (errorInsertar) {
          throw errorInsertar
        }

        tipoEnvaseGuardado = data
      }

      onGuardado?.(tipoEnvaseGuardado)
    } catch (errorGuardado) {
      console.error('Error al guardar caja/rejilla:', errorGuardado)

      setError(
        errorGuardado.message ||
          'No fue posible guardar la caja/rejilla.'
      )
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion ? 'Editar caja/rejilla' : 'Nueva caja/rejilla'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={guardarTipoEnvase}
        >
          <div>
            <label
              htmlFor="nombre"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Nombre
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="nombre"
              type="text"
              value={nombre}
              onChange={(event) => setNombre(event.target.value)}
              placeholder="Ejemplo: Rejilla plástica"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          <div>
            <label
              htmlFor="tara-kg"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Tara (kg)
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="tara-kg"
              type="text"
              inputMode="decimal"
              value={taraKg}
              onChange={(event) => setTaraKg(event.target.value)}
              placeholder="Ejemplo: 1.850"
              disabled={guardando}
              className={estiloInput}
            />

            <p className="mt-1.5 text-xs text-slate-500">
              Peso vacío de una unidad, en kilos. Acepta hasta 3 decimales.
            </p>
          </div>

          {esEdicion && (
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={activo}
                onChange={(event) =>
                  setActivo(event.target.checked)
                }
                disabled={guardando}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Caja/rejilla activa
            </label>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default CajaRejillaModal
